import React from 'react';
import { find } from 'lodash';
import enhance from './CreateCampaign.enhancer';
import Stepper from '@material-ui/core/Stepper';
import { countries } from '../../../constants';
import CampaignAudience from './components/CampaignAudience';
import CampaignDetails from './components/CampaignDetails';
import CampaignPlatform from './components/CampaignPlatform';
import CampaignService from './components/CampaignService';
import CampaignReview from './components/CampaignReview';
import CssBaseline from '@material-ui/core/CssBaseline';
import Paper from '@material-ui/core/Paper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { findDOMNode } from 'react-dom';

const steps = ['Platform', 'Service', 'Audience', 'Details', 'Review'];

class CreateCampaign extends React.Component {
  constructor() {
    super();

    this.initialState = {
      activeStep: 0,
      countries: countries,
      campaignInfo: {
        name: '',
        contentType: null,
        otherContentDetails: '',
        productPhotoUrl: '',
        productCategory: '',
        productUrl: '',
        description: '',
        shippableProduct: true,
        productAndCommission: true,
        commissionOnly: false,
        productOnly: false,
        payoutMin: 0,
        payoutMax: 0,
        duration: 'unlimited',
        brandStory: '',
        goals: '',
        gender: 'All',
        country: 'All',
        ageMin: 14,
        ageMax: 100,
        influenceLevel: 'micro',
        platform: 'youtube',
        isPrivate: false
      }
    };

    this.state = {
      ...this.initialState
    };
  }

  handleNext = () => {
    this.setState(state => ({
      activeStep: state.activeStep + 1
    }));
  };

  handleBack = () => {
    this.setState(state => ({
      activeStep: state.activeStep - 1
    }));
  };

  handleReset = () => {
    this.setState({
      activeStep: 0
    });
  };

  handleCampaignInfoChange = e => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        [e.target.name]: e.target.value
      }
    });
  };

  handleProductPhotoUploadSuccess = productPhotoUrl => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        productPhotoUrl: productPhotoUrl
      }
    });
  };

  shippableProdHandler = isShippableProduct => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        shippableProduct: isShippableProduct
      }
    });
  };

  isPrivateCampaignHandler = isPrivateCampaign => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        isPrivate: isPrivateCampaign
      }
    });
  };

  prodCommissionHandler = e => {
    const key = e.target.value;
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        productAndCommission: key === 'productAndCommission',
        commissionOnly: key === 'commissionOnly',
        productOnly: key === 'productOnly'
      }
    });
  };

  influenceLevelClickHandler = level => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        influenceLevel: level
      }
    });
  };

  contentTypeClickHandler = type => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        contentType: type
      }
    });
  };

  otherContentDescriptionChangeHandler = e => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        otherContentDetails: e.target.value
      }
    });
  };

  genderClickHandler = gender => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        gender
      }
    });
  };

  clearPayouts = () => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        payoutMax: 0,
        payoutMin: 0
      }
    });
  };

  ageSelectionHandler = (event, newValue) => {
    this.setState({
      campaignInfo: {
        ...this.state.campaignInfo,
        ageMin: newValue[0],
        ageMax: newValue[1]
      }
    });
  };

  getStepContent(step, campaignInfo) {
    switch (step) {
      case 0:
        return (
          <CampaignPlatform
            changeContentType={this.contentTypeClickHandler}
            changeOtherContentDescription={
              this.otherContentDescriptionChangeHandler
            }
            campaignInfo={campaignInfo}
            nextButtonClick={this.handleNext}
          />
        );
      case 1:
        return (
          <CampaignService
            campaignInfo={campaignInfo}
            photoUpload={this.handleProductPhotoUploadSuccess}
            changeHandler={this.handleCampaignInfoChange}
            shippableProdHandler={this.shippableProdHandler}
            productCategory={this.state.campaignInfo.productCategory}
            nextButtonClick={this.handleNext}
            backButtonClick={this.handleBack}
          />
        );
      case 2:
        return (
          <CampaignAudience
            campaignInfo={campaignInfo}
            influenceLevelClick={this.influenceLevelClickHandler.bind(this)}
            changeHandler={this.handleCampaignInfoChange}
            genderClick={this.genderClickHandler.bind(this)}
            ageSelectionHandler={this.ageSelectionHandler}
            country={this.state.campaignInfo.country}
            nextButtonClick={this.handleNext}
            backButtonClick={this.handleBack}
          />
        );
      case 3:
        return (
          <CampaignDetails
            campaignInfo={campaignInfo}
            changeHandler={this.handleCampaignInfoChange}
            clearPayouts={this.clearPayouts}
            prodCommissionHandler={this.prodCommissionHandler}
            isPrivateCampaignHandler={this.isPrivateCampaignHandler}
            duration={this.state.campaignInfo.duration}
            nextButtonClick={this.handleNext}
            backButtonClick={this.handleBack}
          />
        );
      case 4:
        return (
          <CampaignReview
            campaignInfo={campaignInfo}
            saveCampaignClick={this.saveCampaign.bind(this)}
            backButtonClick={this.handleBack}
            profile={this.props.profile}
          />
        );
      default:
        throw new Error('Unknown step');
    }
  }

  getHeaderContent(step) {
    switch (step) {
      case 0:
        return (
          <span className={this.props.classes.stepTitleText}>
            Choose the content type for your campaign
          </span>
        );
      case 1:
        return (
          <span className={this.props.classes.stepTitleText}>
            About your campaign's product/service
          </span>
        );
      case 2:
        return (
          <span className={this.props.classes.stepTitleText}>
            What is your target audience?
          </span>
        );
      case 3:
        return (
          <span className={this.props.classes.stepTitleText}>
            Campaign and budget info
          </span>
        );
      case 4:
        return (
          <span className={this.props.classes.stepTitleText}>
            Review Campaign
          </span>
        );
      default:
        throw new Error('Unknown step');
    }
  }

  saveCampaign() {
    const campCountry = find(
      this.state.countries,
      country => country.value === this.state.campaignInfo.country
    );
    const campaignInfo = {
      ...this.state.campaignInfo,
      country: campCountry ? campCountry.key : 'ALL'
    };
    if (campaignInfo.productOnly) {
      campaignInfo.payoutMin = 0;
      campaignInfo.payoutMax = 0;
    }

    this.props.saveCampaignHandler(campaignInfo);
    this.props.history.push('/dashboard');
  }

  render() {
    const { classes, saveCampaignHandler, history } = this.props;
    const { activeStep, campaignInfo } = this.state;

    return (
      <React.Fragment>
        <main className={classes.layout}>
          <Paper className={classes.paper}>
            <Typography component="h1" variant="h4" align="center">
              {this.getHeaderContent(activeStep)}
            </Typography>
            <Stepper activeStep={activeStep} className={classes.stepper}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel className={classes.stepLabel}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <React.Fragment>
              {this.getStepContent(activeStep, campaignInfo)}
            </React.Fragment>
          </Paper>
        </main>
      </React.Fragment>
    );
  }
}

export default enhance(CreateCampaign);



// WEBPACK FOOTER //
// ./src/components/company/campaign/CreateCampaign.js